import { useState, useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { importData as adminImportData, exportCSV } from '../../api/adminClient';

// ============================================================================
// TYPES
// ============================================================================

type ImportType = 'employees' | 'customers' | 'products';

interface ImportError {
  row: number;
  field?: string;
  value?: string;
  message: string;
}

interface ImportResult {
  success: boolean;
  batch_id: string;
  total: number;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  errors: ImportError[];
  duration_ms: number;
}

// ============================================================================
// CONFIG
// ============================================================================

const importTypes: Record<ImportType, { label: string; icon: string; description: string; columns: string[] }> = {
  employees: {
    label: 'Employees',
    icon: '👥',
    description: 'Import employees with their roles and manager relationships',
    columns: ['first_name', 'last_name', 'email', 'manager_email', 'level', 'scope_type', 'is_licensed', 'team'],
  },
  customers: {
    label: 'Customers',
    icon: '🏢',
    description: 'Import customer accounts with ownership assignments',
    columns: ['customer_name', 'website', 'country', 'location', 'industry', 'tier', 'account_owner_emails', 'notes'],
  },
  products: {
    label: 'Products',
    icon: '📦',
    description: 'Import your product catalog',
    columns: ['product_name', 'product_category', 'description'],
  },
};

// ============================================================================
// API FUNCTIONS
// ============================================================================

async function importData(type: ImportType, csvContent: string): Promise<ImportResult> {
  try {
    const result = await adminImportData(type, csvContent);
    return result as ImportResult;
  } catch {
    // Fallback to simulation if API is not available
    return simulateImport(csvContent);
  }
}

async function downloadTemplate(type: ImportType): Promise<void> {
  const templates: Record<ImportType, string> = {
    employees: `first_name,last_name,email,manager_email,level,scope_type,is_licensed,team
John,Doe,john@example.com,jane@example.com,ic1,,true,Platform
Jane,Smith,jane@example.com,,manager,product,true,Platform
Bob,Johnson,bob@example.com,alice@example.com,staff,customer,false,Sales West
Alice,Williams,alice@example.com,,director,customer,true,Sales West`,
    customers: `customer_name,website,country,location,industry,tier,account_owner_emails,notes
Acme Corporation,https://acme.com,United States,San Francisco CA,Technology,enterprise,john@example.com;jane@example.com,Key strategic account
Globex Inc,https://globex.com,United Kingdom,London,Manufacturing,mid-market,bob@example.com,
TechStart,https://techstart.io,United States,Austin TX,Software,smb,alice@example.com,Promising startup`,
    products: `product_name,product_category,description
Widget Pro,Core Platform,Our flagship product for enterprise customers
Analytics Suite,Add-ons,Advanced analytics and reporting module
API Access,Services,Developer API access for integrations`,
  };

  const content = templates[type];
  const blob = new Blob([content], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${type}_import_template.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Simulate import for demo purposes
function simulateImport(csvContent: string): ImportResult {
  const lines = csvContent.trim().split('\n');
  const total = Math.max(0, lines.length - 1); // Exclude header

  return {
    success: true,
    batch_id: `batch-${Date.now()}`,
    total,
    created: Math.floor(total * 0.7),
    updated: Math.floor(total * 0.3),
    skipped: 0,
    failed: 0,
    errors: [],
    duration_ms: 150,
  };
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function AdminImport() {
  const [searchParams] = useSearchParams();
  const initialType = (searchParams.get('type') as ImportType) || 'employees';
  const [selectedType, setSelectedType] = useState<ImportType>(
    ['employees', 'customers', 'products'].includes(initialType) ? initialType : 'employees'
  );
  const [csvContent, setCsvContent] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // Update selected type when URL param changes
  useEffect(() => {
    const typeParam = searchParams.get('type') as ImportType;
    if (typeParam && ['employees', 'customers', 'products'].includes(typeParam)) {
      setSelectedType(typeParam);
    }
  }, [searchParams]);

  const importMutation = useMutation({
    mutationFn: ({ type, content }: { type: ImportType; content: string }) => importData(type, content),
    onSuccess: (data) => {
      setResult(data);
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: [selectedType] });
      queryClient.invalidateQueries({ queryKey: ['command-center'] });
    },
  });

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setResult(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setCsvContent(content);
    };
    reader.readAsText(file);
  };

  const handleImport = () => {
    if (!csvContent) return;
    importMutation.mutate({ type: selectedType, content: csvContent });
  };

  const handleClear = () => {
    setCsvContent('');
    setFileName(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const config = importTypes[selectedType];

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Import Data</h1>
        <p className="text-gray-500 mt-1">
          Upload CSV files to import employees, customers, or products
        </p>
      </div>

      {/* Import Type Selection */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {Object.entries(importTypes).map(([type, config]) => (
          <button
            key={type}
            onClick={() => {
              setSelectedType(type as ImportType);
              handleClear();
            }}
            className={clsx(
              'p-6 rounded-xl border-2 text-left transition-all',
              selectedType === type
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300 bg-white'
            )}
          >
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl">{config.icon}</span>
              <span className="text-lg font-semibold text-gray-900">{config.label}</span>
            </div>
            <p className="text-sm text-gray-500">{config.description}</p>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left: Upload Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>{config.icon}</span> Import {config.label}
          </h2>

          {/* Template Download */}
          <div className="mb-4 p-4 bg-blue-50 border border-blue-100 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-800">Download Template</p>
                <p className="text-xs text-blue-600 mt-1">
                  Get a CSV template with the correct column headers
                </p>
              </div>
              <button
                onClick={() => downloadTemplate(selectedType)}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
              >
                Download
              </button>
            </div>
          </div>

          {/* Export Current Data */}
          <div className="mb-6 p-4 bg-green-50 border border-green-100 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-800">Export Current Data</p>
                <p className="text-xs text-green-600 mt-1">
                  Download existing data as CSV before importing
                </p>
              </div>
              <button
                onClick={async () => {
                  try {
                    const csv = await exportCSV(selectedType);
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${selectedType}_export.csv`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  } catch {
                    // Export not available, use template fallback
                    downloadTemplate(selectedType);
                  }
                }}
                className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
              >
                Export
              </button>
            </div>
          </div>

          {/* File Upload */}
          <div className="mb-6">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
              id="csv-upload"
            />
            <label
              htmlFor="csv-upload"
              className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-500 hover:bg-gray-50 transition-colors"
            >
              {fileName ? (
                <>
                  <span className="text-2xl mb-2">📄</span>
                  <span className="text-sm font-medium text-gray-700">{fileName}</span>
                  <span className="text-xs text-gray-500 mt-1">Click to replace</span>
                </>
              ) : (
                <>
                  <span className="text-2xl mb-2">📤</span>
                  <span className="text-sm font-medium text-gray-700">Click to upload CSV</span>
                  <span className="text-xs text-gray-500 mt-1">or drag and drop</span>
                </>
              )}
            </label>
          </div>

          {/* Preview */}
          {csvContent && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Preview</h3>
              <div className="bg-gray-50 rounded-lg p-3 max-h-48 overflow-auto">
                <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                  {csvContent.split('\n').slice(0, 6).join('\n')}
                  {csvContent.split('\n').length > 6 && '\n...'}
                </pre>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {csvContent.split('\n').length - 1} rows (excluding header)
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleImport}
              disabled={!csvContent || importMutation.isPending}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {importMutation.isPending ? 'Importing...' : `Import ${config.label}`}
            </button>
            {csvContent && (
              <button
                onClick={handleClear}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Right: Results & Info */}
        <div className="space-y-6">
          {/* Results */}
          {result && (
            <div className={clsx(
              'rounded-xl border p-6',
              result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
            )}>
              <h3 className={clsx(
                'text-lg font-semibold mb-4 flex items-center gap-2',
                result.success ? 'text-green-800' : 'text-red-800'
              )}>
                {result.success ? '✅' : '❌'} Import {result.success ? 'Complete' : 'Failed'}
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{result.total}</div>
                  <div className="text-xs text-gray-500">Total Rows</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{result.created}</div>
                  <div className="text-xs text-gray-500">Created</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{result.updated}</div>
                  <div className="text-xs text-gray-500">Updated</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{result.failed}</div>
                  <div className="text-xs text-gray-500">Failed</div>
                </div>
              </div>

              {result.errors.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-red-800 mb-2">Errors:</h4>
                  <div className="bg-white rounded-lg p-3 max-h-40 overflow-auto">
                    {result.errors.map((error, i) => (
                      <div key={i} className="text-xs text-red-600 py-1">
                        Row {error.row}: {error.message}
                        {error.field && ` (${error.field}: ${error.value})`}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-500 mt-4">
                Batch ID: {result.batch_id} | Duration: {result.duration_ms}ms
              </p>
            </div>
          )}

          {/* Column Reference */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">CSV Format</h3>
            <p className="text-sm text-gray-600 mb-4">
              Your CSV file should include the following columns:
            </p>
            <div className="space-y-2">
              {config.columns.map((col, i) => (
                <div
                  key={col}
                  className={clsx(
                    'flex items-center gap-2 text-sm',
                    i < (selectedType === 'employees' ? 3 : 1) ? 'font-medium text-gray-900' : 'text-gray-600'
                  )}
                >
                  <span className={clsx(
                    'w-2 h-2 rounded-full',
                    i < (selectedType === 'employees' ? 3 : 1) ? 'bg-red-500' : 'bg-gray-300'
                  )} />
                  <code className="bg-gray-100 px-2 py-0.5 rounded text-xs">{col}</code>
                  {i < (selectedType === 'employees' ? 3 : 1) && (
                    <span className="text-xs text-red-600">(required)</span>
                  )}
                </div>
              ))}
            </div>

            {selectedType === 'employees' && (
              <>
                <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                  <p className="text-xs text-amber-800">
                    <strong>Tip:</strong> Valid levels are configured in Employees &gt; Manage Levels (e.g. ic1, staff, manager, director)
                  </p>
                </div>
                <div className="mt-2 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                  <p className="text-xs text-amber-800">
                    <strong>Tip:</strong> Team names are auto-created if they don't already exist.
                  </p>
                </div>
              </>
            )}

            {selectedType === 'customers' && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                <p className="text-xs text-amber-800">
                  <strong>Tip:</strong> Valid tiers are: enterprise, mid-market, smb. Use semicolons to separate multiple account owner emails.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
